/**
 * The dashboard's Tickets tab. Reports in this system are tickets (a content or location report
 * files one), so this shows one queue of them, newest first, with a real queue filter in place of
 * the old Filter button that did nothing. A row opens the ticket; "View all" opens the queue page.
 *
 * The ticket endpoints answer with a bare page and no total, so this is one page of the queue
 * rather than a pager over it; the Tickets page is where the whole queue lives.
 */

import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { formatDistanceToNowStrict } from 'date-fns'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { TicketTypeLabels, type TicketSummaryDto } from '@api/types/tickets'
import { TicketStatusBadge } from '@features/tickets/components/TicketStatusBadge'
import { TicketPriorityIndicator } from '@features/tickets/components/TicketPriorityIndicator'
import {
  DASHBOARD_TICKET_QUEUES,
  useDashboardTickets,
  type DashboardTicketQueue,
} from '../../hooks/useDashboardRecent'
import { errorMessage } from '../users/userFormatting'

function age(iso: string): string {
  const date = new Date(iso)
  return isNaN(date.getTime()) ? '-' : formatDistanceToNowStrict(date, { addSuffix: true })
}

export function RecentTicketsTable() {
  const navigate = useNavigate()
  const [queue, setQueue] = useState<DashboardTicketQueue>('pool')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const { data, isLoading, error, refetch } = useDashboardTickets(queue)

  // The pool comes back priority-first; on a dashboard "what came in" reads better newest-first,
  // and priority is a column right next to it.
  const rows = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [data]
  )

  const columns = useMemo<DataTableColumn<TicketSummaryDto>[]>(
    () => [
      {
        key: 'title',
        header: 'Ticket',
        primary: true,
        render: (ticket) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
              {ticket.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {TicketTypeLabels[ticket.type] ?? 'Ticket'}
              {ticket.createdByDisplayName ? ` · ${ticket.createdByDisplayName}` : ''}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'priority',
        header: 'Priority',
        render: (ticket) => <TicketPriorityIndicator priority={ticket.priority} size="small" />,
      },
      {
        key: 'status',
        header: 'Status',
        render: (ticket) => <TicketStatusBadge status={ticket.status} />,
      },
      {
        key: 'age',
        header: 'Opened',
        render: (ticket) => (
          <Typography variant="caption" title={new Date(ticket.createdAt).toLocaleString()}>
            {age(ticket.createdAt)}
          </Typography>
        ),
      },
      {
        key: 'claimedBy',
        header: 'Claimed by',
        hideBelow: 'md',
        render: (ticket) => (
          <Typography variant="caption" color={ticket.claimedByDisplayName ? undefined : 'text.secondary'}>
            {ticket.claimedByDisplayName || ticket.assignedToDisplayName || 'Nobody'}
          </Typography>
        ),
      },
    ],
    []
  )

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowKey={(ticket) => ticket.id}
      loading={isLoading}
      error={error ? errorMessage(error, 'Could not load tickets') : null}
      onRetry={() => refetch()}
      emptyMessage="Nothing in this queue"
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size)
        setPage(0)
      }}
      pageSizeOptions={[5, 10, 25]}
      onRowClick={(ticket) => navigate(`/tickets/${ticket.id}`)}
      getRowActions={(ticket) => [
        {
          key: 'open',
          label: 'Open',
          icon: <OpenInNew />,
          hint: 'Open this ticket',
          onClick: () => navigate(`/tickets/${ticket.id}`),
        },
      ]}
      toolbar={
        <>
          <TextField
            select
            size="small"
            label="Queue"
            value={queue}
            onChange={(e) => {
              setQueue(e.target.value as DashboardTicketQueue)
              setPage(0)
            }}
            sx={{ minWidth: 200, flex: { xs: 1, sm: 'none' } }}
          >
            {DASHBOARD_TICKET_QUEUES.map((q) => (
              <MenuItem key={q.value} value={q.value}>
                {q.label}
              </MenuItem>
            ))}
          </TextField>
          <Button component={RouterLink} to="/tickets" size="small">
            View all
          </Button>
        </>
      }
    />
  )
}
